'use client';

import Link from 'next/link';
import useSWR from 'swr';
import { swrFetcher } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useTranslation } from '../../lib/i18n';
import { useEshopCart } from '../../lib/useEshopCart';
import { buildShopThemeStyle, type DesignConfig } from '../../lib/shopTheme';

type EshopSettings = {
  enabled?: boolean;
  mode?: string;
  shopName?: string;
  introText?: string;
  currency?: string;
  features?: { cart?: boolean; wishlist?: boolean };
  design?: DesignConfig;
};

/**
 * Public e-shop chrome — header with shop name + cart + footer.
 * Rendered instead of the app Layout (route is in isPublicPage list).
 */
export default function ShopLayout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { data: settings } = useSWR<EshopSettings>('/api/public/eshop/settings', swrFetcher);
  const cartEnabled = settings?.features?.cart === true;
  const { cart } = useEshopCart(cartEnabled);
  const themeStyle = buildShopThemeStyle(settings?.design);
  const logoAssetId = settings?.design?.logoAssetId;

  return (
    <div className="min-h-screen bg-cream text-ink font-sans flex flex-col" style={themeStyle}>
      <header className="border-b border-wheat bg-linen">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-6">
          <Link href="/shop" className="text-xl font-semibold text-bark hover:text-wood transition-colors flex items-center gap-2">
            {logoAssetId != null && (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={`/api/public/eshop/assets/${logoAssetId}`} alt="" className="h-8 w-auto object-contain" />
            )}
            {settings?.shopName || t('eshopTitle')}
          </Link>
          <nav className="flex-1 flex gap-4 text-sm text-wood">
            <Link href="/shop" className="hover:text-bark transition-colors">{t('eshopCatalog')}</Link>
            {isAuthenticated && settings?.features?.wishlist === true && (
              <Link href="/shop/wishlist" className="hover:text-bark transition-colors">{t('eshopWishlist')}</Link>
            )}
            {isAuthenticated && cartEnabled && (
              <Link href="/shop/quick-order" className="hover:text-bark transition-colors">{t('eshopQuickOrder')}</Link>
            )}
            {isAuthenticated ? (
              <Link href="/shop/account" className="hover:text-bark transition-colors">{t('eshopMyAccount')}</Link>
            ) : (
              <>
                <Link href="/login?redirect=/shop/account" className="hover:text-bark transition-colors">{t('login')}</Link>
                <Link href="/register?redirect=/shop/account" className="hover:text-bark transition-colors">{t('eshopRegisterLink')}</Link>
              </>
            )}
          </nav>
          {cartEnabled && (
            <Link href="/shop/cart" className="relative text-wood hover:text-bark transition-colors text-sm">
              🛒 {t('eshopCart')}
              {cart.itemCount > 0 && (
                <span className="absolute -top-2 -right-3 bg-rust text-cream text-xs rounded-full min-w-4 h-4 px-1 flex items-center justify-center">
                  {cart.itemCount}
                </span>
              )}
            </Link>
          )}
        </div>
      </header>
      <main className="flex-1 w-full max-w-6xl mx-auto px-4 py-6">
        {children}
      </main>
      <footer className="border-t border-wheat bg-linen">
        <div className="max-w-6xl mx-auto px-4 py-4 text-xs text-wood text-center">
          {settings?.shopName || t('eshopTitle')}
        </div>
      </footer>
    </div>
  );
}
